import useOrganization from "@hooks/useOrganization";
import { useGetPortalDevicesQuery } from "@queries/tanstack/device/DeviceQuery";
import _ from "lodash";
import { useMemo } from "react";
import {
  AI_TYPE,
  ANALYTICS_CHART_TYPE,
  ANALYTICS_CHART_VIEW_TYPE,
  WIDGET_TYPE
} from "@resources/Constants";

import { getWidgetIcon } from "@components/widgets/base/setup/widget-rules/DataMapper";
import { RULE_CACHE_TIME_MS } from "./useAnalyticsRuleData";

export const getRuleType = (chartType: keyof typeof ANALYTICS_CHART_TYPE) => {
  switch (chartType) {
    case ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT:
    case ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY:
      return AI_TYPE.PEOPLE_COUNT;
    case ANALYTICS_CHART_TYPE.PEOPLE_QUEUES:
    case ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC:
      return AI_TYPE.QUEUE_MANAGEMENT;
    case ANALYTICS_CHART_TYPE.SLIP_AND_FALL:
      return AI_TYPE.SLIP_AND_FALL;
    case ANALYTICS_CHART_TYPE.CROWD_COUNTING:
      return AI_TYPE.CROWD_COUNT;
    case ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT:
    case ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE:
      return AI_TYPE.SHOPPING_CART_COUNT;
    case ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES:
      return AI_TYPE.SHOPPING_CART_QUEUE_MANAGEMENT;
    case ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT:
    case ANALYTICS_CHART_TYPE.VEHICLE_OCCUPANCY:
      return AI_TYPE.VEHICLE_COUNT;
    case ANALYTICS_CHART_TYPE.VEHICLE_QUEUES:
      return AI_TYPE.VEHICLE_QUEUE_MANAGEMENT;
    case ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS:
      return AI_TYPE.STOPPED_VEHICLE;
    case ANALYTICS_CHART_TYPE.SLOW_TRAFFIC:
      return AI_TYPE.TRAFFIC_JAM;
    // case ANALYTICS_CHART_TYPE.FORKLIFT_SPEEDING:
    default:
      return null;
  }
};

export const toWidgetType = (chartType: keyof typeof ANALYTICS_CHART_TYPE) => {
  switch (chartType) {
    case ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT:
      return WIDGET_TYPE.CUMULATE_PEOPLECOUNTS;
    case ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY:
      return WIDGET_TYPE.STAYING_PEOPLECOUNTS;
    case ANALYTICS_CHART_TYPE.PEOPLE_QUEUES:
      return WIDGET_TYPE.CHECKOUT_QUEUE;
    case ANALYTICS_CHART_TYPE.SLIP_AND_FALL:
      return WIDGET_TYPE.SLIP_AND_FALL;
    case ANALYTICS_CHART_TYPE.CROWD_COUNTING:
      return WIDGET_TYPE.CROWD_SAFETY_STATISTICS;
    case ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC:
      return WIDGET_TYPE.RETAIL_VISITOR_AREA_TRAFFIC;
    case ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT:
      return WIDGET_TYPE.SHOPPING_CART_COUNT;
    case ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE:
      return WIDGET_TYPE.SHOPPING_CART_COUNT;
    case ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES:
      return WIDGET_TYPE.SHOPPING_CART_QUEUE_MANAGEMENT;
    case ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT:
      return WIDGET_TYPE.CUMULATE_VEHICLECOUNTS;
    case ANALYTICS_CHART_TYPE.VEHICLE_QUEUES:
      return WIDGET_TYPE.VEHICLE_QUEUE;
    case ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS:
      return WIDGET_TYPE.STOPPED_VEHICLE_STATISTICS;
    case ANALYTICS_CHART_TYPE.SLOW_TRAFFIC:
      return WIDGET_TYPE.SLOW_TRAFFIC_STATISTICS;
    case ANALYTICS_CHART_TYPE.VEHICLE_OCCUPANCY:
      return WIDGET_TYPE.STAYING_VEHICLECOUNTS;
    default:
      return null;
  }
};

export const getChartIcon = (chartType: keyof typeof ANALYTICS_CHART_TYPE) => {
  const widgetType = toWidgetType(chartType);
  if (widgetType) {
    return getWidgetIcon(widgetType);
  }

  return null;
};

export const getChartData = (chartType: keyof typeof ANALYTICS_CHART_TYPE) => {
  switch (chartType) {
    case ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT:
    case ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT:
    case ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT:
      return ANALYTICS_CHART_VIEW_TYPE.CUMULATIVE;
    case ANALYTICS_CHART_TYPE.SLIP_AND_FALL:
    case ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY:
    case ANALYTICS_CHART_TYPE.CROWD_COUNTING:
    case ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE:
    case ANALYTICS_CHART_TYPE.SLOW_TRAFFIC:
    case ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS:
      // case ANALYTICS_CHART_TYPE.FORKLIFT_SPEEDING:
      return [];
    case ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES:
    case ANALYTICS_CHART_TYPE.VEHICLE_QUEUES:
    case ANALYTICS_CHART_TYPE.PEOPLE_QUEUES:
    case ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC:
      return ANALYTICS_CHART_VIEW_TYPE.QUEUE;
    default:
      return [];
  }
};

export const useLocationRuleGroupByRuleType = (rule: string[]) => {
  const { orgId } = useOrganization();
  const { data: deviceData, isLoading: isDeviceLoading } =
    useGetPortalDevicesQuery(
      {
        orgId,
        sightmindStatus: true
      },
      {
        enabled: !!orgId,
        staleTime: RULE_CACHE_TIME_MS,
        cacheTime: RULE_CACHE_TIME_MS
      }
    );

  const locations = useMemo(() => {
    if (rule.length > 0 && deviceData) {
      const convertedRule = rule.map((rule) => {
        const [ruleType, deviceId, channel, ruleName] = (rule as string).split(
          ":"
        );
        return {
          deviceId,
          ruleName,
          channel: Number(channel),
          ruleType
        };
      });
      // [TODO] If the location name is the same, it should be calculated as a different value.
      const groupByRuleType = _.groupBy(convertedRule, "ruleType");
      const groupByIds = _.groupBy(deviceData, "id");
      return Object.keys(groupByRuleType)
        .map((key) => {
          const rules = groupByRuleType[key];
          const locations = _.compact(
            _.flatMap(rules, (rule) => {
              const deviceId = rule.deviceId;
              const dinfo = groupByIds[deviceId]?.[0];
              if (!dinfo) {
                return null;
              }

              const locationName = dinfo.locationName;
              const locationId = dinfo.locationId;
              const cameraName = dinfo.name;
              if (locationName && locationId) {
                return {
                  ...rule,
                  locationId,
                  locationName,
                  cameraName
                };
              }

              return null;
            })
          );

          return { [key]: _.groupBy(locations, "locationName") };
        })
        .reduce((acc, item) => {
          return { ...acc, ...item };
        }, {});
    }

    return {};
  }, [deviceData, rule]);

  return {
    data: locations,
    isLoading: isDeviceLoading
  };
};
